import React, { useEffect, useState, Children, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Moon, Clock, Brain, FileText, CheckCircle2, XCircle, ArrowRight, BookOpen, ShieldCheck, PenTool, Battery, X } from 'lucide-react';
// Animation variants
const fadeIn = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};
export function SalesPage() {
  const [modalOpen, setModalOpen] = useState<'privacy' | 'terms' | null>(null);
  // Initialize Meta Pixel and fire PageView on mount
  useEffect(() => {
    // Meta Pixel initialization
    if (typeof window !== 'undefined' && !(window as any).fbq) {
      const f = window as any;
      const n = f.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = '2.0';
      n.queue = [];
      // Load the Facebook Pixel script
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://connect.facebook.net/en_US/fbevents.js';
      document.head.appendChild(script);
      // Initialize and track PageView
      script.onload = () => {
        ;
        (window as any).fbq('init', '826352920446601');
        (window as any).fbq('track', 'PageView');
      };
    } else if ((window as any).fbq) {
      // If already loaded, just track PageView
      ;
      (window as any).fbq('track', 'PageView');
    }
  }, []);
  // Function to track AddToCart event
  const trackAddToCart = () => {
    if (typeof window !== 'undefined' && (window as any).fbq) {
      ;
      (window as any).fbq('track', 'AddToCart', {
        content_name: 'Cierra el Día - Ritual Antirrumiación',
        content_type: 'product',
        value: 6.99,
        currency: 'USD'
      });
    }
  };
  const scrollToOffer = () => {
    const offerSection = document.getElementById('offer');
    if (offerSection) {
      const yOffset = -50; // Offset para que aterrice justo en el título
      const y = offerSection.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({
        top: y,
        behavior: 'smooth'
      });
    }
  };
  const privacyContent = <div className="space-y-6 text-slate-300" data-id="element-2">
      <h3 className="text-2xl font-bold text-white" data-id="element-3">Política de Privacidad</h3>
      <p className="text-sm text-slate-400" data-id="element-4">Última actualización: Enero 2026</p>

      <div className="space-y-4" data-id="element-5">
        <section data-id="element-6">
          <h4 className="text-lg font-semibold text-white mb-2" data-id="element-7">
            1. Información que Recopilamos
          </h4>
          <p data-id="element-8">
            Recopilamos información que nos proporcionas directamente al
            realizar una compra, incluyendo tu nombre, dirección de correo
            electrónico y datos de pago procesados de forma segura por nuestro
            proveedor de pagos.
          </p>
        </section>

        <section data-id="element-9">
          <h4 className="text-lg font-semibold text-white mb-2" data-id="element-10">
            2. Uso de la Información
          </h4>
          <p data-id="element-11">Utilizamos tu información para:</p>
          <ul className="list-disc list-inside mt-2 space-y-1 text-slate-400" data-id="element-12">
            <li data-id="element-13">Procesar y entregar tu compra</li>
            <li data-id="element-14">Enviarte actualizaciones sobre el producto</li>
            <li data-id="element-15">Responder a tus consultas</li>
            <li data-id="element-16">Mejorar nuestros servicios</li>
          </ul>
        </section>

        <section data-id="element-17">
          <h4 className="text-lg font-semibold text-white mb-2" data-id="element-18">
            3. Protección de Datos
          </h4>
          <p data-id="element-19">
            Implementamos medidas de seguridad técnicas y organizativas para
            proteger tu información personal contra acceso no autorizado,
            alteración o destrucción.
          </p>
        </section>

        <section data-id="element-20">
          <h4 className="text-lg font-semibold text-white mb-2" data-id="element-21">4. Cookies</h4>
          <p data-id="element-22">
            Utilizamos cookies esenciales para el funcionamiento del sitio y
            cookies analíticas para entender cómo interactúas con nuestro
            contenido.
          </p>
        </section>

        <section data-id="element-23">
          <h4 className="text-lg font-semibold text-white mb-2" data-id="element-24">
            5. Tus Derechos
          </h4>
          <p data-id="element-25">
            Tienes derecho a acceder, corregir o eliminar tu información
            personal. También puedes solicitar la portabilidad de tus datos o
            retirar tu consentimiento en cualquier momento.
          </p>
        </section>

        <section data-id="element-26">
          <h4 className="text-lg font-semibold text-white mb-2" data-id="element-27">6. Terceros</h4>
          <p data-id="element-28">
            No vendemos ni compartimos tu información personal con terceros,
            excepto proveedores de servicios necesarios para operar nuestro
            negocio (procesadores de pago, servicios de email).
          </p>
        </section>
      </div>
    </div>;
  const termsContent = <div className="space-y-6 text-slate-300" data-id="element-29">
      <h3 className="text-2xl font-bold text-white" data-id="element-30">Términos y Condiciones</h3>
      <p className="text-sm text-slate-400" data-id="element-31">Última actualización: Enero 2026</p>

      <div className="space-y-4" data-id="element-32">
        <section data-id="element-33">
          <h4 className="text-lg font-semibold text-white mb-2" data-id="element-34">
            1. Aceptación de Términos
          </h4>
          <p data-id="element-35">
            Al acceder y utilizar este sitio web y realizar una compra, aceptas
            estar sujeto a estos términos y condiciones de uso.
          </p>
        </section>

        <section data-id="element-36">
          <h4 className="text-lg font-semibold text-white mb-2" data-id="element-37">
            2. Descripción del Producto
          </h4>
          <p data-id="element-38">
            "Cierra el Día" es un producto digital educativo en formato PDF que
            incluye guías, plantillas y recursos para mejorar tus hábitos de
            sueño. Este producto no constituye consejo médico profesional.
          </p>
        </section>

        <section data-id="element-39">
          <h4 className="text-lg font-semibold text-white mb-2" data-id="element-40">
            3. Entrega del Producto
          </h4>
          <p data-id="element-41">
            Tras completar tu compra, recibirás acceso inmediato al producto
            digital a través de un enlace de descarga enviado a tu correo
            electrónico.
          </p>
        </section>

        <section data-id="element-42">
          <h4 className="text-lg font-semibold text-white mb-2" data-id="element-43">
            4. Política de Reembolso
          </h4>
          <p data-id="element-44">
            Debido a la naturaleza digital del producto, no ofrecemos reembolsos
            una vez que el producto ha sido descargado. Si tienes problemas
            técnicos para acceder al producto, contáctanos y te ayudaremos.
          </p>
        </section>

        <section data-id="element-45">
          <h4 className="text-lg font-semibold text-white mb-2" data-id="element-46">
            5. Uso Personal
          </h4>
          <p data-id="element-47">
            El producto es para uso personal únicamente. No está permitido
            redistribuir, revender o compartir el contenido sin autorización
            expresa.
          </p>
        </section>

        <section data-id="element-48">
          <h4 className="text-lg font-semibold text-white mb-2" data-id="element-49">
            6. Propiedad Intelectual
          </h4>
          <p data-id="element-50">
            Todo el contenido, incluyendo textos, gráficos, plantillas y
            diseños, está protegido por derechos de autor y es propiedad
            exclusiva de Cierra el Día.
          </p>
        </section>

        <section data-id="element-51">
          <h4 className="text-lg font-semibold text-white mb-2" data-id="element-52">
            7. Limitación de Responsabilidad
          </h4>
          <p data-id="element-53">
            Este producto es material educativo y no sustituye el consejo médico
            profesional. No nos hacemos responsables de resultados individuales,
            ya que estos dependen de múltiples factores personales.
          </p>
        </section>

        <section data-id="element-54">
          <h4 className="text-lg font-semibold text-white mb-2" data-id="element-55">
            8. Modificaciones
          </h4>
          <p data-id="element-56">
            Nos reservamos el derecho de modificar estos términos en cualquier
            momento. Los cambios entrarán en vigor inmediatamente después de su
            publicación en el sitio.
          </p>
        </section>
      </div>
    </div>;
  return <div className="min-h-screen bg-[#0a1628] text-slate-200 font-sans selection:bg-[#d4a574] selection:text-[#0a1628]" data-id="element-57">
      {/* Modal */}
      <AnimatePresence data-id="element-58">
        {modalOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setModalOpen(null)} data-id="element-59">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.2
        }} className="bg-[#1a2744] rounded-2xl border border-blue-800/50 max-w-2xl w-full max-h-[80vh] overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-60">
              <div className="flex items-center justify-between p-6 border-b border-blue-800/50" data-id="element-61">
                <div className="flex items-center gap-2 text-[#d4a574]" data-id="element-62">
                  <Moon size={20} data-id="element-63" />
                  <span className="font-semibold" data-id="element-64">Cierra el Día</span>
                </div>
                <button onClick={() => setModalOpen(null)} className="text-slate-400 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/10" data-id="element-65">
                  <X size={24} data-id="element-66" />
                </button>
              </div>
              <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)]" data-id="element-67">
                {modalOpen === 'privacy' ? privacyContent : termsContent}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* 1. HERO SECTION */}
      <section className="relative pt-20 pb-16 px-4 md:pt-32 md:pb-24 overflow-hidden" data-id="element-68">
        {/* Background ambient glow */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[500px] bg-blue-900/20 blur-[120px] rounded-full pointer-events-none" data-id="element-69" />

        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center relative z-10" data-id="element-70">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="text-left space-y-6" data-id="element-71">
            <motion.div variants={fadeIn} className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#1a2744] border border-blue-800/50 text-[#d4a574] text-sm font-medium" data-id="element-72">
              <Moon size={14} data-id="element-73" />
              <span data-id="element-74">
                Ritual Antirrumiación de 10 a 15 Minutos para Dormir Profundo
              </span>
            </motion.div>

            <motion.h1 variants={fadeIn} className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight" data-id="element-75">
              Tu problema no es dormir.{' '}
              <span className="text-[#d4a574]" data-id="element-76">
                Es que tu día nunca termina.
              </span>
            </motion.h1>

            <motion.p variants={fadeIn} className="text-lg md:text-xl text-slate-300 leading-relaxed max-w-lg" data-id="element-77">
              Cuando el cuerpo se apaga pero la mente sigue prendida, no
              necesitas "relajarte". Necesitas un cierre.
            </motion.p>

            <motion.div variants={fadeIn} className="flex flex-col sm:flex-row gap-4 items-start sm:items-center pt-4" data-id="element-78">
              <button onClick={scrollToOffer} className="bg-[#d4a574] hover:bg-[#c29463] text-[#0a1628] font-bold py-4 px-8 rounded-lg text-lg transition-all shadow-[0_0_20px_rgba(212,165,116,0.3)] hover:shadow-[0_0_30px_rgba(212,165,116,0.5)] flex items-center gap-2" data-id="element-79">
                Obtener Cierra el Día
                <ArrowRight size={20} data-id="element-80" />
              </button>
              <div className="flex flex-col" data-id="element-81">
                <div className="flex items-center gap-2" data-id="element-82">
                  <span className="text-sm text-slate-500 line-through" data-id="element-83">
                    $14.99
                  </span>
                  <span className="text-2xl font-bold text-white" data-id="element-84">$6.99</span>
                </div>
                <span className="text-xs text-[#d4a574] uppercase tracking-wider font-medium" data-id="element-85">
                  ¡Gran Descuento!
                </span>
              </div>
            </motion.div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="relative" data-id="element-86">
            <div className="relative z-10 rounded-xl overflow-hidden shadow-2xl border border-blue-800/30 bg-[#1a2744]" data-id="element-87">
              <img src="https://cdn.magicpatterns.com/uploads/cfFePTmwX7v3XjxCcpsoVq/Cierra_El_Dia.png" alt="Cierra el Día Tablet Mockup" className="w-full h-auto object-cover" data-id="element-88" />
            </div>
            {/* Decorative elements behind image */}
            <div className="absolute -inset-4 bg-gradient-to-tr from-[#d4a574]/20 to-transparent blur-2xl -z-10 rounded-full opacity-60" data-id="element-89" />
          </motion.div>
        </div>
      </section>

      {/* 2. OPENING LEAD SECTION */}
      <section className="py-16 px-4 bg-[#0d1b30]" data-id="element-90">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeIn} className="max-w-2xl mx-auto text-center space-y-8" data-id="element-91">
          <p className="text-xl md:text-2xl text-slate-300 font-light italic" data-id="element-92">
            "Te acuestas cansado. Y justo cuando por fin hay silencio… empieza
            la reunión."
          </p>

          <div className="space-y-6 text-lg text-slate-300 leading-relaxed text-left" data-id="element-93">
            <p data-id="element-94">
              La mente abre el día otra vez: pendientes, conversaciones,
              cuentas, cosas que "debiste" hacer, y ese miedo sutil de que
              mañana vas a pagar el precio.
            </p>
            <p className="font-medium text-white text-xl border-l-4 border-[#d4a574] pl-4" data-id="element-95">
              No es falta de disciplina. Es falta de{' '}
              <span className="text-[#d4a574]" data-id="element-96">cierre</span>.
            </p>
            <p data-id="element-97">
              Porque si tu cerebro siente que el día quedó abierto, no se
              duerme: se queda de guardia.
            </p>
          </div>
        </motion.div>
      </section>

      {/* 3. PROBLEM AGITATION */}
      <section className="py-20 px-4 bg-[#0a1628]" data-id="element-98">
        <div className="max-w-4xl mx-auto" data-id="element-99">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="text-center mb-12" data-id="element-100">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-101">
              Esto no es "otro PDF de dormir mejor"
            </h2>
            <p className="text-slate-400" data-id="element-102">
              La mayoría de cosas de sueño asumen que tu problema es el café, la
              luz azul o "una rutina perfecta".
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-103">
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={staggerContainer} className="space-y-4" data-id="element-104">
              <p className="text-lg font-medium text-[#d4a574] mb-4" data-id="element-105">
                Pero si tú eres de los que:
              </p>
              {['Se acuesta y la mente se prende', 'Repite conversaciones como si fueran un juicio', 'Se va al futuro con escenarios catastróficos', "Siente culpa por no 'hacer suficiente'"].map((item, i) => <motion.div key={i} variants={fadeIn} className="flex items-start gap-3 bg-[#1a2744]/50 p-4 rounded-lg border border-blue-900/50" data-id="element-106">
                  <XCircle className="text-red-400 shrink-0 mt-1" size={20} data-id="element-107" />
                  <span className="text-slate-200" data-id="element-108">{item}</span>
                </motion.div>)}
            </motion.div>

            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeIn} className="bg-[#1a2744] p-8 rounded-2xl border border-[#d4a574]/20 shadow-xl relative overflow-hidden" data-id="element-109">
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#d4a574]/10 rounded-full blur-3xl -mr-16 -mt-16 pointer-events-none" data-id="element-110" />

              <h3 className="text-2xl font-bold text-white mb-4" data-id="element-111">
                El Diagnóstico Real
              </h3>
              <p className="text-slate-300 mb-6 leading-relaxed" data-id="element-112">
                Tu problema no es una técnica bonita. Tu problema es que tu
                mente llega a la noche con{' '}
                <strong className="text-white" data-id="element-113">el día abierto</strong>.
              </p>
              <div className="bg-[#0a1628] p-4 rounded-lg border-l-4 border-[#d4a574]" data-id="element-114">
                <p className="text-lg font-medium text-white italic" data-id="element-115">
                  "No puedo soltar. Hay cosas importantes sin contenedor."
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 4. WHY OLD SOLUTIONS FAIL */}
      <section className="py-20 px-4 bg-[#0d1b30] border-y border-blue-900/30" data-id="element-116">
        <div className="max-w-5xl mx-auto" data-id="element-117">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="text-center mb-16" data-id="element-118">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-119">
              Por qué lo que has probado no se sostiene
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-120">
            {[{
            title: '"Relájate"',
            desc: 'No es un plan concreto, es solo un deseo vago.'
          }, {
            title: 'Meditaciones largas',
            desc: 'A las 11pm se sienten como otra tarea más en tu lista.'
          }, {
            title: 'Pantalla / Alcohol',
            desc: 'Apaga por ratos, pero no cierra nada: el problema rebota.'
          }, {
            title: 'Intentar no pensar',
            desc: "Convierte la cama en batalla… y tu cerebro aprende: 'noche = guerra'."
          }].map((card, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="bg-[#0a1628] p-6 rounded-xl border border-blue-900 hover:border-[#d4a574]/50 transition-colors group" data-id="element-121">
                <div className="w-10 h-1 bg-blue-800 group-hover:bg-[#d4a574] mb-4 transition-colors rounded-full" data-id="element-122" />
                <h3 className="text-xl font-bold text-white mb-3" data-id="element-123">
                  {card.title}
                </h3>
                <p className="text-slate-400 text-sm leading-relaxed" data-id="element-124">
                  {card.desc}
                </p>
              </motion.div>)}
          </div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="mt-12 text-center" data-id="element-125">
            <p className="text-xl text-slate-200" data-id="element-126">
              No necesitas motivación. Necesitas un{' '}
              <span className="text-[#d4a574] font-bold" data-id="element-127">
                procedimiento corto
              </span>
              , repetible, y diseñado para cuando estás cansado.
            </p>
          </motion.div>
        </div>
      </section>

      {/* 5. THE 3C METHOD REVEAL */}
      <section className="py-24 px-4 bg-gradient-to-b from-[#0a1628] to-[#1a2744]" data-id="element-128">
        <div className="max-w-6xl mx-auto" data-id="element-129">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="text-center mb-16" data-id="element-130">
            <span className="text-[#d4a574] font-medium tracking-wider uppercase text-sm mb-2 block" data-id="element-131">
              La Solución
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6" data-id="element-132">
              Presentando: Cierra el Día{' '}
              <span className="text-[#d4a574]" data-id="element-133">(Método 3C)</span>
            </h2>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto" data-id="element-134">
              Un ritual anti‑rumiación de 10–15 minutos para marcarle a tu
              cerebro: <br data-id="element-135" />
              <span className="text-white font-semibold" data-id="element-136">
                "Se acabó. Ya está contenido. Mañana sigo."
              </span>
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-137">
            {/* C1 */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="bg-[#0a1628] rounded-2xl p-8 border border-blue-800 relative overflow-hidden group hover:-translate-y-2 transition-transform duration-300" data-id="element-138">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#d4a574] to-transparent opacity-50" data-id="element-139" />
              <div className="w-14 h-14 bg-[#1a2744] rounded-full flex items-center justify-center mb-6 text-[#d4a574] group-hover:bg-[#d4a574] group-hover:text-[#0a1628] transition-colors" data-id="element-140">
                <PenTool size={28} data-id="element-141" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2" data-id="element-142">
                C1 — Capturar
              </h3>
              <p className="text-slate-400 mb-4 text-sm uppercase tracking-wide" data-id="element-143">
                Contención Mental
              </p>
              <p className="text-slate-300 leading-relaxed" data-id="element-144">
                Sacas lo que tu mente sostiene y lo bajas a papel con un{' '}
                <strong data-id="element-145">Próximo Paso Mínimo</strong>. No para resolver. Para
                quitarle a tu cabeza el trabajo de "acordarse".
              </p>
            </motion.div>

            {/* C2 */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="bg-[#0a1628] rounded-2xl p-8 border border-blue-800 relative overflow-hidden group hover:-translate-y-2 transition-transform duration-300" data-id="element-146">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#d4a574] to-transparent opacity-50" data-id="element-147" />
              <div className="w-14 h-14 bg-[#1a2744] rounded-full flex items-center justify-center mb-6 text-[#d4a574] group-hover:bg-[#d4a574] group-hover:text-[#0a1628] transition-colors" data-id="element-148">
                <Battery size={28} data-id="element-149" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2" data-id="element-150">
                C2 — Calmar
              </h3>
              <p className="text-slate-400 mb-4 text-sm uppercase tracking-wide" data-id="element-151">
                Regulación Física
              </p>
              <p className="text-slate-300 leading-relaxed" data-id="element-152">
                Bajas activación del cuerpo en <strong data-id="element-153">3–5 minutos</strong>.
                Porque si el cuerpo sigue en alerta, la mente seguirá fabricando
                problemas para justificar esa alerta.
              </p>
            </motion.div>

            {/* C3 */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3
          }} className="bg-[#0a1628] rounded-2xl p-8 border border-blue-800 relative overflow-hidden group hover:-translate-y-2 transition-transform duration-300" data-id="element-154">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#d4a574] to-transparent opacity-50" data-id="element-155" />
              <div className="w-14 h-14 bg-[#1a2744] rounded-full flex items-center justify-center mb-6 text-[#d4a574] group-hover:bg-[#d4a574] group-hover:text-[#0a1628] transition-colors" data-id="element-156">
                <Brain size={28} data-id="element-157" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2" data-id="element-158">
                C3 — Conducir
              </h3>
              <p className="text-slate-400 mb-4 text-sm uppercase tracking-wide" data-id="element-159">
                Enfoque Único
              </p>
              <p className="text-slate-300 leading-relaxed" data-id="element-160">
                Diriges tu mente a <strong data-id="element-161">un solo canal</strong> para cortar el
                loop. No es "vaciar la mente". Es dejar de abrir 20 pestañas y
                enfocarse en una sola segura.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 6. TRANSFORMATION SECTION */}
      <section className="py-20 px-4 bg-[#0a1628]" data-id="element-162">
        <div className="max-w-4xl mx-auto" data-id="element-163">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="bg-[#1a2744]/30 rounded-3xl p-8 md:p-12 border border-blue-900/50" data-id="element-164">
            <h2 className="text-3xl font-bold text-white mb-8 text-center" data-id="element-165">
              Lo que cambia cuando cierras el día
            </h2>

            <div className="space-y-6 mb-10" data-id="element-166">
              {['Una forma de cortar la rumiación sin discutir con tu mente', 'Un plan concreto para cuando te despiertas en la madrugada', "Una rutina que no depende de 'estar inspirado' o motivado", 'Recuperar la cama como lugar de descanso, no de trabajo'].map((benefit, i) => <div key={i} className="flex items-start gap-4" data-id="element-167">
                  <CheckCircle2 className="text-[#d4a574] shrink-0 mt-1" size={24} data-id="element-168" />
                  <p className="text-lg text-slate-200" data-id="element-169">{benefit}</p>
                </div>)}
            </div>

            <div className="text-center border-t border-blue-800/50 pt-8" data-id="element-170">
              <p className="text-xl font-medium text-white" data-id="element-171">
                No te prometo magia. Te prometo{' '}
                <span className="text-[#d4a574]" data-id="element-172">dirección</span>.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 7. WHAT'S INCLUDED */}
      <section className="py-20 px-4 bg-[#0d1b30]" data-id="element-173">
        <div className="max-w-5xl mx-auto" data-id="element-174">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="text-center mb-12" data-id="element-175">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-176">
              Qué recibes hoy
            </h2>
            <p className="text-slate-400" data-id="element-177">
              Descarga inmediata • Acceso de por vida
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8" data-id="element-178">
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeIn} className="bg-[#0a1628] p-8 rounded-2xl border border-blue-900 flex flex-col justify-center" data-id="element-179">
              <ul className="space-y-4" data-id="element-180">
                {[{
                icon: FileText,
                text: "El diagnóstico del 'día abierto' (para entender tu patrón)"
              }, {
                icon: Clock,
                text: 'El ritual 3C paso a paso (10–15 min)'
              }, {
                icon: PenTool,
                text: 'Plantillas imprimibles (1 página simple)'
              }, {
                icon: ShieldCheck,
                text: 'Tarjeta de Rescate Nocturno (para cama y madrugada)'
              }, {
                icon: Moon,
                text: 'Plan de instalación de 14 noches (sin perfección)'
              }, {
                icon: Brain,
                text: 'Ajustes por tipo de mente: perfeccionista, ansiosa, etc.'
              }].map((item, i) => <li key={i} className="flex items-center gap-3 text-slate-300" data-id="element-181">
                    <item.icon className="text-[#d4a574] shrink-0" size={20} data-id="element-182" />
                    <span data-id="element-183">{item.text}</span>
                  </li>)}
              </ul>
            </motion.div>

            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeIn} className="relative rounded-2xl overflow-hidden border border-blue-900 shadow-2xl" data-id="element-184">
              <img src="https://cdn.magicpatterns.com/uploads/cfFePTmwX7v3XjxCcpsoVq/Cierra_El_Dia.png" alt="Materiales incluidos" className="w-full h-full object-cover" data-id="element-185" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a1628] via-transparent to-transparent opacity-60" data-id="element-186" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* 8. WHO IT'S FOR */}
      <section className="py-20 px-4 bg-[#0a1628]" data-id="element-187">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-8" data-id="element-188">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="bg-[#1a2744]/40 p-8 rounded-2xl border-l-4 border-[#d4a574]" data-id="element-189">
            <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-2" data-id="element-190">
              <CheckCircle2 className="text-[#d4a574]" data-id="element-191" /> Es para ti si:
            </h3>
            <p className="text-slate-300 text-lg leading-relaxed" data-id="element-192">
              Eres hispano/a 30+ en USA, tienes un trabajo de alta
              responsabilidad, estás saturado, y sientes que tu mente no sabe
              cómo terminar el día laboral y personal.
            </p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="bg-[#1a2744]/40 p-8 rounded-2xl border-l-4 border-slate-600" data-id="element-193">
            <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-2" data-id="element-194">
              <XCircle className="text-slate-400" data-id="element-195" /> No es para ti si:
            </h3>
            <p className="text-slate-300 text-lg leading-relaxed" data-id="element-196">
              Tienes sospecha de apnea del sueño, insomnio severo crónico sin
              evaluación médica, o crisis intensas frecuentes. Esto es un
              sistema de hábitos, no reemplaza atención profesional.
            </p>
          </motion.div>
        </div>
      </section>

      {/* 9. FINAL OFFER SECTION */}
      <section id="offer" className="py-24 px-4 bg-gradient-to-b from-[#0d1b30] to-[#0a1628] relative overflow-hidden" data-id="element-197">
        {/* Decorative glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#d4a574]/5 blur-[100px] rounded-full pointer-events-none" data-id="element-198" />

        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeIn} className="max-w-3xl mx-auto text-center relative z-10 bg-[#1a2744]/80 backdrop-blur-sm p-8 md:p-12 rounded-3xl border border-[#d4a574]/30 shadow-[0_0_50px_rgba(0,0,0,0.5)]" data-id="element-199">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-2" data-id="element-200">
            Cierra el Día
          </h2>
          <p className="text-[#d4a574] text-xl font-medium mb-8" data-id="element-201">
            Ritual Anti‑Rumiación para Dormir Profundo (3C)
          </p>

          <div className="flex justify-center items-baseline gap-3 mb-2" data-id="element-202">
            <span className="text-2xl text-slate-500 line-through" data-id="element-203">$14.99</span>
            <span className="text-6xl font-bold text-white" data-id="element-204">$6.99</span>
            <span className="text-slate-400" data-id="element-205">USD</span>
          </div>
          <p className="text-[#d4a574] font-medium mb-8" data-id="element-206">¡Gran Descuento!</p>

          <div className="flex flex-col md:flex-row justify-center gap-8 mb-10 text-left md:text-center" data-id="element-207">
            <div className="flex items-center gap-2 justify-center" data-id="element-208">
              <BookOpen size={20} className="text-[#d4a574]" data-id="element-209" />
              <span className="text-slate-300" data-id="element-210">Formato PDF + Plantillas</span>
            </div>
            <div className="flex items-center gap-2 justify-center" data-id="element-211">
              <Clock size={20} className="text-[#d4a574]" data-id="element-212" />
              <span className="text-slate-300" data-id="element-213">Acceso Inmediato</span>
            </div>
          </div>

          <a href="https://www.multimundodigital.com/cart/51947678400811:1" target="_blank" rel="noopener noreferrer" onClick={trackAddToCart} className="w-full md:w-auto bg-[#d4a574] hover:bg-[#c29463] text-[#0a1628] font-bold py-5 px-10 rounded-xl text-xl transition-all shadow-[0_0_20px_rgba(212,165,116,0.3)] hover:shadow-[0_0_40px_rgba(212,165,116,0.5)] hover:scale-[1.02] active:scale-[0.98] inline-block text-center" data-id="element-214">
            Descargar "Cierra el Día" Ahora
          </a>

          <p className="mt-6 text-sm text-slate-400" data-id="element-215">
            Si esta noche estás cansado pero tu mente no se calla, no necesitas
            otra charla motivacional. Necesitas cerrar el día.
          </p>
        </motion.div>
      </section>

      {/* 10. FOOTER */}
      <footer className="py-12 px-4 bg-[#050b14] text-center border-t border-blue-900/30" data-id="element-216">
        <div className="max-w-4xl mx-auto space-y-6" data-id="element-217">
          <div className="flex items-center justify-center gap-2 text-[#d4a574] font-bold text-xl" data-id="element-218">
            <Moon size={24} data-id="element-219" />
            <span data-id="element-220">Cierra el Día</span>
          </div>

          <div className="flex items-center justify-center gap-6" data-id="element-221">
            <button onClick={() => setModalOpen('privacy')} className="text-slate-400 hover:text-[#d4a574] transition-colors text-sm underline-offset-4 hover:underline" data-id="element-222">
              Política de Privacidad
            </button>
            <span className="text-slate-600" data-id="element-223">•</span>
            <button onClick={() => setModalOpen('terms')} className="text-slate-400 hover:text-[#d4a574] transition-colors text-sm underline-offset-4 hover:underline" data-id="element-224">
              Términos y Condiciones
            </button>
          </div>

          <p className="text-slate-500 text-sm" data-id="element-225">
            © {new Date().getFullYear()} Cierra el Día. Todos los derechos
            reservados.
          </p>
          <p className="text-slate-600 text-xs max-w-lg mx-auto" data-id="element-226">
            Este producto es material educativo y no sustituye el consejo médico
            profesional. Si sufres de insomnio crónico o problemas de salud
            mental, consulta a un especialista.
          </p>
        </div>
      </footer>
    </div>;
}